import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import type { ReactNode } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { usePromoteTicket, extractErrorMessage } from '@/queries/ticketMutations'
import { useTrackerCategories } from '@/queries/tracker'

const schema = z.object({
  category: z.string().min(1, 'Choose a category'),
  impact: z.string().optional(),
})
type FormVals = z.infer<typeof schema>

export function PromoteDialog({
  ticketId,
  companyId,
  trigger,
  onPromoted,
}: {
  ticketId: string
  companyId: string
  trigger: ReactNode
  onPromoted?: () => void
}) {
  const [open, setOpen] = useState(false)
  const promote = usePromoteTicket(ticketId)
  // Deferred until the dialog is actually open — companyId is undefined (and
  // the query disabled, see useTrackerCategories) while closed, so mounting
  // this dialog ahead of a click (e.g. inside TrackerControls) never fires a
  // request nobody asked for.
  const categoriesQuery = useTrackerCategories(open ? companyId : undefined)
  const categories = categoriesQuery.data ?? []
  const form = useForm<FormVals>({
    resolver: zodResolver(schema),
    defaultValues: { category: '', impact: '' },
  })

  const onSubmit = form.handleSubmit(async values => {
    try {
      await promote.mutateAsync({ category: values.category, impact: values.impact?.trim() || undefined })
      toast.success('Ticket added to the tracker')
      setOpen(false)
      form.reset()
      onPromoted?.()
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not promote ticket'))
    }
  })

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        setOpen(next)
        if (!next) form.reset()
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Track this issue</DialogTitle>
          <DialogDescription>
            Promoting this ticket adds it to the client tracker. Once tracked, this ticket and its full
            conversation — every reply, not just a summary — becomes visible to everyone at the client&apos;s
            company, not only the people currently on the thread.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4">
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange} disabled={categoriesQuery.isLoading}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={categoriesQuery.isLoading ? 'Loading categories…' : 'Choose a category'} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categories.map(category => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="impact"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Impact (optional)</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} placeholder="What's the impact on the client?" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={promote.isPending || categoriesQuery.isLoading}>
                {promote.isPending ? 'Promoting…' : 'Track this issue'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
